import { forwardRef } from 'react';

import { cn } from '../../utils/styles';

import InputBox from './components/InputBox';
import InputError from './components/InputError';
import InputLabel from './components/InputLabel';
import { InputClassNames } from './constants';
import { InputRoot } from './Input.styled';
import { InputProps } from './types';

const Input = forwardRef<HTMLInputElement, InputProps>((props, inputRef) => {
  const {
    className,
    'data-testid': dataTestId,
    elementSize = 'large',
    error,
    isLabelHidden,
    label,
    tooltipLabel,
    isErrorHidden,
    isErrorInitiallyHidden: isErrorSpaceNotReserved,
    icon,
    leading,
    trailing,
    isDisabled: isDisabledProp,
    isRequired: isRequiredProp,
    disabled,
    required,
    'aria-invalid': ariaInvalid,
    hasNoDisabledStyle,
    ...inputProps
  } = props;

  const isDisabled = disabled || isDisabledProp;
  const isRequired = required || isRequiredProp;

  const hasError = !!error || (!!ariaInvalid && ariaInvalid !== 'false');

  return (
    <InputRoot
      className={cn(InputClassNames.Root, className)}
      data-testid={dataTestId}>
      <InputLabel
        hasError={hasError}
        isDisabled={isDisabled && !hasNoDisabledStyle}
        isLabelHidden={isLabelHidden}
        label={label}
        tooltipLabel={tooltipLabel}
      />
      <InputBox
        elementSize={elementSize}
        hasError={hasError}
        hasNoDisabledStyle={hasNoDisabledStyle}
        inputProps={{
          disabled: isDisabled,
          required: isRequired,
          'aria-label': label,
          ...inputProps,
        }}
        inputRef={inputRef}
        leading={leading || icon}
        trailing={trailing}
      />
      <InputError
        elementSize={elementSize}
        error={error}
        inputTestId={dataTestId}
        isErrorHidden={isErrorHidden}
        isErrorSpaceReserved={!isErrorSpaceNotReserved}
      />
    </InputRoot>
  );
});

Input.displayName = 'Input';

export default Input;
